/**
 * Auth0 errors that mean the stored session can never produce an access token again.
 *
 * The distinction this module exists to draw is between *this session is finished* and
 * *this attempt failed*. Both arrive as a rejected `getAccessTokenSilently()`, and treating
 * them alike is wrong in both directions: signing a user out over a dropped connection is
 * hostile, and keeping a session that can never mint a token again strands them.
 *
 * - `missing_refresh_token` — the cache holds a user but no refresh token. Seen on a
 *   packaged-app build whose session predated Allow Offline Access, and on any client whose
 *   refresh token was rotated away or never issued.
 * - `login_required` / `consent_required` — Auth0 will only continue with a visible
 *   interaction, which by definition silent auth cannot do.
 * - `invalid_grant` — the refresh token was revoked, expired, or already used.
 *
 * Anything else — a network failure, a timeout, an Auth0 outage — is *not* in this list and
 * must leave the session alone. It is a bad moment, not a dead session, and the next
 * navigation may well succeed.
 */
const UNUSABLE_SESSION_ERRORS = [
   'missing_refresh_token',
   'login_required',
   'consent_required',
   'invalid_grant',
];

/**
 * Whether a failed token fetch means the session itself is unusable.
 *
 * Auth0's SDK errors carry the OAuth code on an `error` property. A plain `Error` — which is
 * what a network failure surfaces as — has no such property and returns false, so the
 * caller keeps the session.
 */
export function isUnusableAuthSession(error: unknown): boolean {
   const code = (error as { error?: unknown } | null | undefined)?.error;

   if (typeof code !== 'string') return false;

   return UNUSABLE_SESSION_ERRORS.includes(code);
}
